<template>
  <div class="productSidebar">
    <div class="productSidebar__top">
      <div v-if="isMultiPacks && !$device.isMobileOrTablet" class="productSidebar__multi">
        <ProductMultiPacksList :options="preparedOptions" :product="product" />
      </div>
      <div v-if="isMultiPacks && $device.isMobileOrTablet" class="productSidebar__multi">
        <ProductMultiPacksSelect :options="preparedOptions" :product="product" />
      </div>
      <div class="productSidebar__header">
        <product-price
          class="productSidebar__headerPrice"
          :price-display="product.priceObject"
          :price-before-discount="product.priceBeforeDiscountObject"
          :unit-price="
            isMultiPacks && currentMultipack?.unitPrice ? currentMultipack.unitPrice : undefined
          "
          :unit-discount="isMultiPacks ? currentMultipack?.discount : undefined"
          :uom="isMultiPacks ? currentMultipack?.uom : undefined"
          :price-per-base-uom="product.pricePerBaseUOM"
        />
      </div>
      <div class="productSidebar__catalogSubset" v-if="!product.canBeOrdered && hasCatalogSubset">
        <div class="productSidebar__catalogSubsetPrice">Produkt spoza minikatalogu</div>
        <catalog-subset-banner-small />
      </div>

      <div v-if="product.priceBeforeDiscountObject" class="productSidebar__priceInfo">
        Najniższa cena z 30 dni:
        <span v-if="product.priceBeforeDiscountObject.currencyPrefix">{{
          product.priceBeforeDiscountObject.currencyPrefix
        }}</span
        >{{ product.priceBeforeDiscountObject.grossFormatted }}
        <span v-if="product.priceBeforeDiscountObject.currencyPostfix">{{
          product.priceBeforeDiscountObject.currencyPostfix
        }}</span>
      </div>

      <div v-if="product.canBeOrdered" class="productSidebar__actions">
        <div :class="['productSidebar__action', 'productSidebar__actionButtons']">
          <manual-add-to-basket
            :uom="product.uom !== 'paleta' ? product.uom : ''"
            @addToBasket="val => cartButtonClickHandler(product, val)"
            :available-stock="
              product.canBeOrdered && product.availableStock > 0 ? product.availableStock : 0
            "
          />
        </div>
        <template v-if="outlet && outlet.length > 0">
          <div v-for="(item, index) in outlet" :key="index" class="productSidebar__outlet">
            <div class="productSidebar__outletTitle">Opcje produktu:</div>
            <NuxtLink
              class="productSidebar__outletButton productSidebar__outletButton--outlet"
              :to="localePath({ name: 'product-slug', params: { slug: item.slug } })"
            >
              <div class="productSidebar__outletButtonTitle">Outlet</div>
              <div class="productSidebar__outletButtonPrice">
                <span>
                  {{
                    showNetPrices ? item.priceObject.netFormatted : item.priceObject.grossFormatted
                  }}
                </span>
                {{ item.priceObject.currencyPostfix }}
                {{ showNetPrices ? $t('net') : $t('gross') }}
                <span class="productSidebar__buttonArrow icon-chevron-right"></span>
              </div>
            </NuxtLink>
          </div>
        </template>
        <div v-if="mainProduct" class="productSidebar__outlet">
          <div class="productSidebar__outletTitle">Opcje produktu:</div>
          <NuxtLink
            class="productSidebar__outletButton"
            :to="localePath({ name: 'product-slug', params: { slug: mainProduct.slug } })"
          >
            <div class="productSidebar__outletButtonTitle">Pełnowartościowy</div>
            <div class="productSidebar__outletButtonPrice">
              <span>
                {{
                  showNetPrices
                    ? mainProduct.priceObject.netFormatted
                    : mainProduct.priceObject.grossFormatted
                }}
              </span>
              {{ mainProduct.priceObject.currencyPostfix }}
              {{ showNetPrices ? $t('net') : $t('gross') }}
              <span class="productSidebar__buttonArrow icon-chevron-right"></span>
            </div>
          </NuxtLink>
        </div>
      </div>
    </div>
    <div
      :class="['productSidebar__info', { 'productSidebar__info--noOrder': !product.canBeOrdered }]"
    >
      <template v-if="product.canBeOrdered && product.availableStock > 0">
        <div
          @click="
            pushModal({
              modal: buyNowModal,
              data: { dateText: 'jutro', endDateTimestamp: product.shippingTimestamp },
            })
          "
          class="productSidebar__infoButton"
          v-if="product.shippingTimestamp"
        >
          <span class="productSidebar__infoButtonIcon icon-clock" />
          <div class="productSidebar__infoButtonText">
            <strong>Kup teraz, otrzymasz jutro</strong> <br />
            Pozostało
            <client-only>
              <countdown
                :end-date="product.shippingTimestamp"
                class="productSidebar__countdownTime"
              />
            </client-only>
          </div>
        </div>
        <div
          @click="
            pushModal({
              modal: buyNowModal,
              data: { dateText: product.availabilityTimeAsString },
            })
          "
          class="productSidebar__infoButton"
          v-else-if="product.availabilityTimeAsString"
        >
          <span class="productSidebar__infoButtonIcon icon-clock" />
          <div class="productSidebar__infoButtonText">
            <strong>Kup teraz, otrzymasz {{ product.availabilityTimeAsString }}</strong
            ><br />
            {{ $t('findOutMore') }}
          </div>
        </div>
      </template>
      <button
        v-if="product.freeShipping.gross || product.freeShipping.net"
        class="productSidebar__infoButton"
        @click="
          pushModal({
            modal: deliveryModal,
            data: {
              productId: product.id,
              productImage: product.gallery.main.url80x60,
              productName: product.name,
              productFreeShipping: isFreeShipping,
              productFreeShippingFrom: product.freeShipping.grossFormatted,
              productDeliveryFee: product.deliveryFee.grossFormatted,
            },
          })
        "
      >
        <span class="productSidebar__infoButtonIcon icon-delivery" />
        <span class="productSidebar__infoButtonText">
          <template v-if="!isFreeShipping">
            <strong v-if="!product.deliveryFee.gross">{{ $t('freeDeliveryFrom') }} </strong>
            <strong v-else
              >{{ $t('freeDeliveryFrom') }} {{ $t('from') }}
              <span v-if="businessVariables">
                {{
                  showNetPrices
                    ? businessVariables.freeShipping.netFormatted
                    : businessVariables.freeShipping.grossFormatted
                }}
                {{ businessVariables.freeShipping.currencyPostfix }}
                {{ showNetPrices ? $t('net') : $t('gross') }}
              </span>
              <span v-if="businessVariables?.freeShippingDeliveryMethod"
                >dla {{ businessVariables.freeShippingDeliveryMethod.label }}</span
              >
            </strong>
          </template>
          <template v-else><strong> Darmowa dostawa </strong></template>
          <br />
          {{ $t('findOutMore') }}
        </span>
      </button>
      <button class="productSidebar__infoButton" @click="pushModal({ modal: paymentModal })">
        <span class="productSidebar__infoButtonIcon icon-payment" />
        <span class="productSidebar__infoButtonText">
          <strong>Płać, tak jak lubisz</strong><br />
          {{ $t('findOutMore') }}
        </span>
      </button>
      <div class="productSidebar__infoButton productSidebar__infoButton--noPointer">
        <span class="productSidebar__infoButtonIcon icon-renew" style="font-size: 16px" />
        <span class="productSidebar__infoButtonText">
          <strong>Zwrot - 14dni</strong>
        </span>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters, mapState } from 'vuex';
import ProductPrice from './ProductPrice';
import FormCountInput from '~/components/Form/FormCountInput';
import cartMixin from '~/mixins/cart';
import VButton from '~/components/VButton';
import Countdown from '~/components/Countdown';
import AddListButton from '~/components/AddListButton';
import ProductMultiPacksSelect from '~/components/Product/ProductMultiPacksSelect';
import ProductMultiPacksList from '@/components/Product/ProductMultiPacksList.vue';
import ManualAddToBasket from '@/components/ManualAddToBasket.vue';
import CatalogSubsetBannerSmall from '@/components/CatalogSubset/CatalogSubsetBannerSmall.vue';

export default {
  name: 'ProductSidebar',
  components: {
    CatalogSubsetBannerSmall,
    ManualAddToBasket,
    ProductMultiPacksList,
    ProductPrice,
    FormCountInput,
    VButton,
    Countdown,
    AddListButton,
    ProductMultiPacksSelect,
  },
  mixins: [cartMixin],
  props: {
    isOnShoppingList: {
      type: Boolean,
      default: false,
    },
    product: {
      type: Object,
      default: () => {},
    },
    outlet: {
      type: Array,
      default: () => [],
    },
    mainProduct: {
      type: Object,
      default: () => {},
    },
  },
  data() {
    return {
      buyNowModal: {
        name: 'buyNow',
        path: 'Product/ModalBuyNow',
        title: 'Termin dostawy',
        footerDisabled: true,
      },
      deliveryModal: {
        name: 'delivery',
        path: 'Product/ModalDelivery',
        title: 'Dostawa',
        footerDisabled: true,
      },
      paymentModal: {
        name: 'payment',
        path: 'Product/ModalPayment',
        title: 'Metody płatności',
        footerDisabled: true,
      },
      disableMultipleInputGroup: true,
      preparedOptions: [],
      currentMultipack: undefined,
    };
  },
  computed: {
    ...mapGetters('auth', ['showNetPrices', 'hasCatalogSubset']),
    ...mapGetters('cart', ['shoppingCartItems']),
    ...mapState({
      businessVariables: state => state.settings.businessVariables,
      shoppingCart: state => state.cart.shoppingCart,
    }),
    isFreeShipping() {
      return this.product.priceObject.gross >= this.product.freeShipping.gross;
    },
    isMultiPacks() {
      return (
        typeof this.product.multiPacks !== 'undefined' &&
        Array.isArray(this.product.multiPacks) &&
        this.product.multiPacks.length
      );
    },
  },
  created() {
    if (this.isMultiPacks) {
      this.preparedOptions = this.$helper.prepareMultiPacks(this.product);
      this.currentMultipack = this.preparedOptions.find(item => item.id === this.product.id);
    }
  },
  methods: {
    async cartButtonClickHandler(product, count) {
      this.isLoading = true;
      await this.addProductToCart(product, count);
      this.isLoading = false;
    },
  },
};
</script>

<style lang="scss">
.productSidebar {
  $root: &;
  pointer-events: all;
  display: flex;
  flex-wrap: wrap;
  background-color: $cWhite;

  &__top {
    border-top: 1px solid $cAddLight;
    flex: 0 0 50%;
    max-width: 50%;

    @include rwd('laptop', true) {
      border-top: 0;
      flex: 0 0 100%;
      max-width: 100%;
    }

    @include rwd('tablet') {
      border-top: 0;
      flex: 0 0 100%;
      max-width: 100%;
    }
  }

  &__multi {
    padding: 2rem;

    @include rwd('laptop', true) {
      border-bottom: 1px solid $cAddLight;
    }

    @include rwd('small-tablet') {
      padding: 2rem 0;
    }

    @include rwd('large-phone') {
      padding: 1rem 0;
    }
  }

  &__header {
    display: flex;
    flex-wrap: wrap;
    align-items: flex-end;
    padding: 2rem 2rem 0 2rem;

    @include rwd('small-tablet') {
      padding: 2rem 0 0 0;
    }
  }

  &__headerPrice {
    text-align: right;
    margin-left: auto;

    @include rwd('small-tablet') {
      margin-bottom: 0.7rem;
      flex: 0 0 100%;
      max-width: 100%;
      text-align: left;
    }
  }

  &__priceInfo {
    padding: 1rem 2rem 0 2rem;
    font-size: 1.2rem;
    color: $cMainNeutral;
    text-align: right;

    @include rwd('small-tablet') {
      padding: 1rem 0 0 0;
    }
  }

  &__actions {
    padding: 1.5rem 2rem 2rem;

    @include rwd('small-tablet') {
      padding: 1.5rem 0 2rem;
    }
  }

  &__action {
    margin-bottom: 10px;

    &:last-child {
      margin-bottom: 0;
    }
  }

  &__info {
    flex: 0 0 50%;
    max-width: 50%;
    border-top: 1px solid $cAddLight;

    @include rwd('laptop', true) {
      flex: 0 0 100%;
      max-width: 100%;
    }

    @include rwd('tablet') {
      border-top: 0;
      flex: 0 0 100%;
      max-width: 100%;
    }

    &--noOrder {
      border-top: none;
    }
  }

  &__infoButton {
    display: flex;
    align-items: center;
    width: 100%;
    cursor: pointer;

    &--noPointer {
      cursor: default;
    }

    @include rwd('small-tablet') {
      border-bottom: 1px solid $cAddLight;

      &:last-child {
        border-bottom: 0;
      }
    }

    &:last-child {
      #{$root}__infoButtonText {
        border-bottom: 0;
      }
    }
  }

  &__infoButtonIcon {
    flex: 0 0 60px;
    max-width: 60px;
    font-size: 2.2rem;
    color: $cSec;
    text-align: center;
  }

  &__infoButtonText {
    flex: 1;
    padding-top: 1.4rem;
    padding-bottom: 1.4rem;
    text-align: left;
    font-size: 1.4rem;
    line-height: 2rem;
    color: $cAddDark02;
    border-bottom: 1px solid $cAddLight;

    @include rwd('small-tablet') {
      border-bottom: 0;
    }

    strong {
      color: $cMainDark;
    }
  }

  &__countdownTime {
    display: inline-block;
  }

  &__catalogSubset {
    padding: 0 2rem 0 2rem;
    margin-bottom: 1rem;
  }

  &__catalogSubsetPrice {
    text-align: right;
    font-size: 1.3rem;
    line-height: 1.25;
    padding-top: 0.6rem;
  }

  &__outlet {
    margin-top: 2rem;
  }

  &__outletTitle {
    margin-bottom: 0.5rem;
    font-size: 1.4rem;
    line-height: 1.7;
    font-weight: 700;
  }

  &__outletButton {
    display: flex;
    align-items: center;
    border: 1px solid $cAddLight;
    border-top-right-radius: 0.4rem;
    border-bottom-right-radius: 0.4rem;
    width: 100%;
    padding: 2rem 1.5rem;
    position: relative;
    cursor: pointer;
    transition: opacity $tr;

    &:hover {
      opacity: 0.75;
    }

    &--outlet {
      &::before {
        content: '';
        position: absolute;
        top: -1px;
        left: -1px;
        height: calc(100% + 2px);
        border-left: 2px solid $cBadgeProm;
      }
    }
  }

  &__outletButtonTitle {
    font-size: 1.4rem;
    line-height: 1;
    color: $cMainNeutral;
    font-weight: 700;
  }

  &__outletButtonPrice {
    margin-left: auto;
    font-size: 1.4rem;
    line-height: 1.4;

    span {
      font-weight: 700;
      font-size: 1.8rem;
      line-height: 1.1;
    }
  }

  &__buttonArrow.icon-chevron-right {
    margin-left: 0.4rem;
    color: $cAddDark02;

    &:before {
      font-size: 1.2rem;
      font-weight: 700;
    }
  }
}
</style>
